import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import commandsData from "../data/private/commands.json";
import { logActivity } from "../utils/activity";
import useModalBehavior from "../utils/useModalBehavior";
import { isFeatureEnabled, usePlatformContext } from "../context/PlatformContext";
import { isUiFeatureVisible } from "../utils/featureControl";

const ACTION_TARGETS = {
  "add-client": {
    route: "/dashboard/clients",
    storageKey: "myv_action_add_client",
  },
  "add-property": {
    route: "/dashboard/properties",
    storageKey: "myv_action_add_property",
  },
  "start-analysis": {
    route: "/dashboard/analysis",
  },
  "generate-report": {
    route: "/dashboard/reports",
  },
};

export default function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const modalRef = useRef(null);
  const { features } = usePlatformContext();

  const featureKeyByTarget = useMemo(
    () => ({
      "/dashboard/reports": "reports",
      "/dashboard/billing": "billing",
      "generate-report": "reports",
    }),
    []
  );

  const filteredGroups = useMemo(() => {
    const term = query.trim().toLowerCase();
    return commandsData.groups
      .map((group) => {
        const items = group.items.filter((item) => {
          const match = item.label.toLowerCase().includes(term);
          if (!match) return false;
          const key = featureKeyByTarget[item.target];
          if (!key) return true;
          if (!isFeatureEnabled(features, key)) return false;
          return isUiFeatureVisible(key);
        });
        return { ...group, items };
      })
      .filter((group) => group.items.length > 0);
  }, [featureKeyByTarget, features, query]);

  const flatItems = useMemo(
    () =>
      filteredGroups.flatMap((group) =>
        group.items.map((item) => ({ ...item, groupLabel: group.label }))
      ),
    [filteredGroups]
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      const isCmdOrCtrl = event.metaKey || event.ctrlKey;
      if (isCmdOrCtrl && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen(true);
        setQuery("");
        setActiveIndex(0);
        logActivity("command_opened", { source: "shortcut" });
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  useModalBehavior(open, handleClose, modalRef);

  const executeCommand = (command) => {
    if (!command) return;
    logActivity("command_executed", {
      label: command.label,
      type: command.type,
      target: command.target,
    });
    if (command.type === "route") {
      navigate(command.target);
    } else {
      const action = ACTION_TARGETS[command.target];
      if (action?.storageKey) {
        localStorage.setItem(action.storageKey, "true");
      }
      window.dispatchEvent(
        new CustomEvent("myv_command_action", { detail: command.target })
      );
      if (action?.route) {
        navigate(action.route);
      }
    }
    setOpen(false);
  };

  const handleKeyNavigation = (event) => {
    if (!flatItems.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((prev) => (prev + 1) % flatItems.length);
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((prev) =>
        prev === 0 ? flatItems.length - 1 : prev - 1
      );
    }
    if (event.key === "Enter") {
      event.preventDefault();
      executeCommand(flatItems[activeIndex]);
    }
    if (event.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 px-4 pt-24"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <motion.div
            ref={modalRef}
            className="card w-full max-w-xl p-4"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2">
              <span className="text-sm">{commandsData.search.icon}</span>
              <input
                ref={inputRef}
                className="w-full text-sm outline-none"
                placeholder={commandsData.search.placeholder}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setActiveIndex(0);
                }}
                onKeyDown={handleKeyNavigation}
              />
            </div>
            <div className="mt-4 space-y-4">
              {filteredGroups.length === 0 ? (
                <p className="text-sm text-slate-500">
                  {commandsData.emptyLabel}
                </p>
              ) : (
                filteredGroups.map((group, groupIndex) => (
                  <div key={`${group.label}-${groupIndex}`} className="space-y-2">
                    <p className="text-xs font-semibold uppercase text-slate-400">
                      {group.label}
                    </p>
                    <div className="space-y-1">
                      {group.items.map((item) => {
                        const itemIndex = flatItems.findIndex(
                          (entry) =>
                            entry.label === item.label && entry.type === item.type
                        );
                        const isActive = itemIndex === activeIndex;
                        return (
                          <button
                            key={`${item.label}-${item.type}`}
                            className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition ${
                              isActive
                                ? "bg-amber-50 text-amber-700"
                                : "text-slate-600 hover:bg-slate-100"
                            }`}
                            onClick={() => executeCommand(item)}
                            type="button"
                          >
                            <span className="flex items-center gap-2">
                              <span>{item.icon}</span>
                              <span>{item.label}</span>
                            </span>
                            <span className="text-xs text-slate-400">
                              {item.type}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
